import { cn } from "./classNames-DcGnTh2N.js";
import { Button } from "./Button-D2JqBAq7.js";
import { TextInput } from "./TextInput-K2DSDs5O.js";
import { Switch } from "./Switch-Dw4tvQls.js";
import { Select } from "./Select-CjQgU_6K.js";
import { Checkbox } from "./Checkbox-uFE8XLT_.js";
import { FieldItem, fieldContext, formContext, useFieldContext, useFormContext } from "./FieldItem-CYvJyjiI.js";
import { NumberInput } from "./internal/blocks/NumberInput.js";
import { Textarea } from "./internal/blocks/Textarea.js";
import { PasswordInput } from "./internal/blocks/PasswordInput.js";
import { OtpInput } from "./OtpInput-CY8yCaJk.js";
import { Alert } from "./Alert-CZ0dDsuk.js";
import { jsx } from "react/jsx-runtime";
import { createFormHook } from "@tanstack/react-form";
//#region src/blocks/Form/components/FormBody.tsx
const FormBody = ({ children, className, ...formProps }) => {
	const form = useFormContext();
	return /* @__PURE__ */ jsx("form", {
		...formProps,
		className: cn("tw:flex tw:flex-col tw:gap-4", className),
		onSubmit: (e) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		children
	});
};
//#endregion
//#region src/blocks/Form/components/SubmitButton.tsx
const SubmitButton = ({ children = "Submit", ...buttonProps }) => {
	const form = useFormContext();
	return /* @__PURE__ */ jsx(form.Subscribe, {
		selector: (state) => ({
			isSubmitting: state.isSubmitting,
			canSubmit: state.canSubmit
		}),
		children: ({ isSubmitting, canSubmit }) => /* @__PURE__ */ jsx(Button, {
			...buttonProps,
			type: "submit",
			isLoading: isSubmitting,
			disabled: !canSubmit || isSubmitting,
			children
		})
	});
};
//#endregion
//#region src/blocks/Form/components/SubmitErrorAlert.tsx
/**
* Renders the form-level submit error (set via `setSubmitError` → `errorMap.onSubmit`) as an error
* `Alert`. Place inside `form.FormBody`, typically just above the submit button. Renders nothing when
* there is no submit error.
*/
const SubmitErrorAlert = () => {
	const form = useFormContext();
	return /* @__PURE__ */ jsx(form.Subscribe, {
		selector: (state) => state.errorMap.onSubmit,
		children: (submitError) => typeof submitError === "string" ? /* @__PURE__ */ jsx(Alert, {
			variant: "error",
			title: submitError
		}) : null
	});
};
//#endregion
//#region src/blocks/Form/connectors/CheckboxField.tsx
const CheckboxField = ({ className, children, ...checkboxProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		className,
		children: (control) => /* @__PURE__ */ jsx(Checkbox, {
			...checkboxProps,
			...control,
			checked: field.state.value ?? false,
			onCheckedChange: (checked) => field.handleChange(checked === true),
			onBlur: field.handleBlur,
			children
		})
	});
};
//#endregion
//#region src/blocks/Form/connectors/NumberField.tsx
const NumberField = ({ label, action, required, requiredMark, help, tooltip, className, "aria-describedby": ariaDescribedBy, ...inputProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		label,
		action,
		required,
		requiredMark,
		help,
		tooltip,
		className,
		"aria-describedby": ariaDescribedBy,
		children: (control) => /* @__PURE__ */ jsx(NumberInput, {
			...inputProps,
			...control,
			value: field.state.value ?? null,
			onValueChange: field.handleChange,
			onBlur: field.handleBlur
		})
	});
};
//#endregion
//#region src/blocks/Form/connectors/OtpInputField.tsx
const OtpInputField = ({ label, action, required, requiredMark, help, tooltip, className, "aria-describedby": ariaDescribedBy, maxLength, ...otpProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		label,
		action,
		required,
		requiredMark,
		help,
		tooltip,
		className,
		"aria-describedby": ariaDescribedBy,
		children: (control) => /* @__PURE__ */ jsx(OtpInput, {
			...otpProps,
			...control,
			maxLength,
			value: field.state.value ?? "",
			onChange: field.handleChange,
			onBlur: field.handleBlur,
			children: /* @__PURE__ */ jsx(OtpInput.Group, { children: Array.from({ length: maxLength }, (_, index) => /* @__PURE__ */ jsx(OtpInput.Slot, { index }, index)) })
		})
	});
};
//#endregion
//#region src/blocks/Form/connectors/PasswordField.tsx
const PasswordField = ({ label, action, required, requiredMark, help, tooltip, className, "aria-describedby": ariaDescribedBy, ...inputProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		label,
		action,
		required,
		requiredMark,
		help,
		tooltip,
		className,
		"aria-describedby": ariaDescribedBy,
		children: (control) => /* @__PURE__ */ jsx(PasswordInput, {
			...inputProps,
			...control,
			value: field.state.value ?? "",
			onValueChange: field.handleChange,
			onBlur: field.handleBlur
		})
	});
};
//#endregion
//#region src/blocks/Form/connectors/SelectField.tsx
const SelectField = ({ label, action, required, requiredMark, help, tooltip, className, "aria-describedby": ariaDescribedBy, triggerClassName, ...selectProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		label,
		action,
		required,
		requiredMark,
		help,
		tooltip,
		className,
		"aria-describedby": ariaDescribedBy,
		children: (control) => /* @__PURE__ */ jsx(Select, {
			...selectProps,
			...control,
			className: triggerClassName ?? "tw:w-full",
			value: field.state.value ?? "",
			onValueChange: field.handleChange,
			onBlur: field.handleBlur
		})
	});
};
//#endregion
//#region src/blocks/Form/connectors/SwitchField.tsx
const SwitchField = ({ className, children, ...switchProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		className,
		children: (control) => /* @__PURE__ */ jsx(Switch, {
			...switchProps,
			...control,
			checked: field.state.value ?? false,
			onCheckedChange: field.handleChange,
			onBlur: field.handleBlur,
			children
		})
	});
};
//#endregion
//#region src/blocks/Form/connectors/TextareaField.tsx
const TextareaField = ({ label, action, required, requiredMark, help, tooltip, className, "aria-describedby": ariaDescribedBy, ...inputProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		label,
		action,
		required,
		requiredMark,
		help,
		tooltip,
		className,
		"aria-describedby": ariaDescribedBy,
		children: (control) => /* @__PURE__ */ jsx(Textarea, {
			...inputProps,
			...control,
			value: field.state.value ?? "",
			onValueChange: field.handleChange,
			onBlur: field.handleBlur
		})
	});
};
//#endregion
//#region src/blocks/Form/connectors/TextField.tsx
const TextField = ({ label, action, required, requiredMark, help, tooltip, className, "aria-describedby": ariaDescribedBy, ...inputProps }) => {
	const field = useFieldContext();
	return /* @__PURE__ */ jsx(FieldItem, {
		label,
		action,
		required,
		requiredMark,
		help,
		tooltip,
		className,
		"aria-describedby": ariaDescribedBy,
		children: (control) => /* @__PURE__ */ jsx(TextInput, {
			...inputProps,
			...control,
			value: field.state.value ?? "",
			onValueChange: field.handleChange,
			onBlur: field.handleBlur
		})
	});
};
//#endregion
//#region src/blocks/Form/formHook.ts
/**
* Typed form hook + `withForm` binding for forms with validation, server errors or a submit state.
*
* - Render `field.*` inside `form.AppForm` → `form.FormBody`; submit via `form.SubmitButton`.
* - `ComboboxField`, `MultiComboboxField`, `CodeEditorField` are imported by name: no tree-shaking.
* - Not for a lone `<input>` (`TextInput`) or an unvalidated label + input (`FormGroup`).
* - Takes any [Standard Schema](https://standardschema.dev/) validator.
*
* @see RECIPES.md#modal-form
*/
const { useAppForm, withForm } = createFormHook({
	fieldContext,
	formContext,
	fieldComponents: {
		TextField,
		NumberField,
		PasswordField,
		TextareaField,
		SelectField,
		CheckboxField,
		SwitchField,
		OtpInputField
	},
	formComponents: {
		FormBody,
		SubmitButton,
		SubmitErrorAlert
	}
});
//#endregion
//#region src/blocks/Form/setServerFieldErrors.ts
/**
* Map a list of server-returned field errors onto a TanStack form instance.
*
* - Marks each field blurred — that is what `FieldItem` gates the error reveal on.
* - `isTouched` alongside it, for anything reading TanStack's own meta (`canSubmit`, dirty checks).
* - Both are needed. Called from a mutation's `onError` there is no submit attempt to fall back on, so
*   without `isBlurred` the message would sit in `errorMap` and render nowhere.
*/
const setServerFieldErrors = (form, errors) => {
	for (const { field, message } of errors) form.setFieldMeta(field, (meta) => ({
		...meta,
		isBlurred: true,
		isTouched: true,
		errors: [message],
		errorMap: {
			...meta.errorMap,
			onServer: message
		}
	}));
};
//#endregion
//#region src/blocks/Form/setSubmitError.ts
/**
* Set a **form-level** submit error on a TanStack form instance — a failed submission that isn't tied
* to a single field (bad credentials, 5xx, network). Stored on `errorMap.onSubmit` and rendered by
* `SubmitErrorAlert`. For field-specific server errors use `setServerFieldErrors` instead.
*
* Note: any error entry makes the form "invalid", so pair this with `canSubmitWhenInvalid: true` on
* the form to keep the submit button retryable (a resubmit re-runs the validators and clears it).
*/
const setSubmitError = (form, message) => {
	form.setErrorMap({ onSubmit: {
		form: message,
		fields: {}
	} });
};
//#endregion
export { setServerFieldErrors, setSubmitError, useAppForm, withForm };

//# sourceMappingURL=Form-CD55zJiy.js.map